import {Component, EventEmitter, HostListener, Input, OnInit, Output} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {take} from 'rxjs/operators';
import {Sidenav} from 'src/app/models/sidenav.model';
import * as BatchlineSelectionActions from '../../actions/batchline-selection.actions';
import {BatchlineSelection} from '../../models/batchline-selection';
import {IKpiLine} from '../../models/kpi.model';
import {Iproject} from '../../models/project.model';
import * as fromRoot from '../../reducers';
import {MobileCheck} from '../../services/mobile-check/mobile-check.service';
import {Iproduct} from "../../models/product.model";
import {ProjectService} from "../../services/project/project.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AriaLivePoliteness} from "@angular/cdk/a11y";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {UpdateBatchStateModel} from "../../models/update-batch-state.model";
import {IconsComponent} from "../../shared/components/icons/icons.component";
import {ColorComponent} from "../../shared/components/color/color.component";
import {LineNamePipe} from "../../pipes/line-name/line-name.pipe";
import {MatIcon} from "@angular/material/icon";
import {LoaderComponent} from "../../shared/components/loader/loader.component";
import {DashboardControlsComponent} from "../dashboard";
import {KpiComponent} from "../kpi/kpi.component";
import {MatCheckbox} from "@angular/material/checkbox";
import {NgClass} from "@angular/common";
import {MatTooltip} from "@angular/material/tooltip";
/**
 * Represents the project header.
 * Handles back-button logic.
 */
@Component({
  selector: 'colimo-project-header',
  styleUrls: ['project-header.component.scss'],
  templateUrl: 'project-header.component.html',
  imports: [
    IconsComponent,
    ColorComponent,
    TranslateModule,
    MatIcon,
    LineNamePipe,
    LoaderComponent,
    DashboardControlsComponent,
    KpiComponent,
    MatTooltip,
    MatCheckbox,
    NgClass
  ]
})
export class ProjectHeaderComponent implements OnInit {
  @Input() public project: Iproject;
  @Input() public lineId: string;
  @Input() public modelId: string;
  @Input() public kpis: IKpiLine;
  @Input() public showProductSelection: boolean = false;
  @Input() public showProductCCNGUpdate: boolean = false;

  @Output() public productSelectionEvent: EventEmitter<number[]> = new EventEmitter(); // BCH-533622 - changed to number[]
  @Output() public batchStateUpdateEvent: EventEmitter<UpdateBatchStateModel> = new EventEmitter();

  // BCH-533622 - store product IDs instead of names
  public selectedProductIds: number[] = [];

  public arrowColor: string;

  public routeParams$: Observable<any>;
  public currentRouteParams;

  public isMobile: boolean;
  public isColorRoute: boolean;

  public loadingLabMeasurements: boolean = false;

  @HostListener('window:popstate', ['$event'])
  onPopState() {
    // call back method also for browser back events
    this.goBack();
  }

  private static readonly colors = {
    desktop: '#2272A5',
    mobile: '#2272A5',
  };
  private activeDashboard: string;

  constructor(
    private mobileCheck: MobileCheck,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<fromRoot.State>,
    private projectService: ProjectService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
  }

  public ngOnInit(): void {
    this.mobileCheck.getScreenSize().subscribe((device) => {
      this.arrowColor = device.isMobile ? ProjectHeaderComponent.colors.mobile : ProjectHeaderComponent.colors.desktop;
      this.isMobile = device.isMobile;
    });

    this.routeParams$ = this.route.params;
    this.routeParams$.subscribe((route) => {
      this.currentRouteParams = route;

      // BCH-533622 - Restore saved product filter from store when returning from details
      this.store
        .select(fromRoot.getProductFilter)
        .pipe(take(1))
        .subscribe((productFilter) => {
          this.selectedProductIds = productFilter.selectedProductsByProject[route.projectId] || [];
        });
    });

    this.store.select(fromRoot.getActiveDashboard).subscribe((activeDashboard) => {
      this.activeDashboard = activeDashboard;
    });
    this.store.select(fromRoot.getSidenav).subscribe((sidenav: Sidenav) => {
      this.isColorRoute = sidenav.isColorRoute;
    });
  }

  // BCH-533622 - now uses product ID instead of name
  public productSelectionChanged(event, product: Iproduct): void {
    if (event.checked) {
      if (!this.selectedProductIds.includes(product.id)) {
        this.selectedProductIds = [...this.selectedProductIds, product.id];
      }
    } else {
      this.selectedProductIds = this.selectedProductIds.filter(id => id !== product.id);
    }
    this.productSelectionEvent.emit(this.selectedProductIds);
  }

  // BCH-533622 - check by product ID instead of name
  public productSelected(product: Iproduct): boolean {
    return this.selectedProductIds.includes(product.id);
  }

  public goBack(): void {
    if (this.currentRouteParams.chartType || this.currentRouteParams.vector) {
      let routerLink = ['/project', this.currentRouteParams.projectId];
      if (this.isColorRoute) {
        routerLink = routerLink.concat(['color']);
      }
      if (this.lineId && !this.isColorRoute) {
        routerLink = routerLink.concat(['line', this.lineId]);
      }
      if (this.modelId && !this.isColorRoute) {
        routerLink = routerLink.concat(['model', this.modelId]);
      }
      if (this.currentRouteParams.projectPage) {
        routerLink = routerLink.concat(['page', this.currentRouteParams.projectPage]);
      }
      this.router.navigate(routerLink);
    } else {
      // reset batch line selection when going back to the dashboards
      this.store.dispatch(new BatchlineSelectionActions.SetSelectionAction({} as BatchlineSelection));
      if (this.modelId && !this.isColorRoute) {
        this.router.navigate(['/model']);
      } else if (this.lineId && !this.isColorRoute) {
        this.router.navigate(['/paintline']);
      } else {
        this.router.navigate(['/color']);
      }
    }
  }

  // #501084 Button to pull from CCNG a new batch in status "examination"
  // loads new lab measurements from ccng if new measurements are available
  public refreshLabMeasurements(product: Iproduct): void {
    this.loadingLabMeasurements = true;
    this.projectService.updateLabMeasurementsByProductId({productId: product.id}).subscribe({
      next: (numberOfNewMeasurements: number) => {
        if (numberOfNewMeasurements > 0) {
          // if new measurements were loaded update batch states
          this.updateBatchState(product);
        } else {
          this.showMsg(numberOfNewMeasurements + ' ' + this.translate.instant('dashboard.updatedForecastMeasurements'));
          this.loadingLabMeasurements = false;
        }
      },
      error: () => {
        this.loadingLabMeasurements = false;
        this.showMsg(this.translate.instant('dashboard.errorUpdatingForecastMeasurements'), 5000, 'assertive');
      },
      complete: () => {
      },
    });
  }

  // #501084 Button to pull from CCNG a new batch in status "examination"
  // method is called when new lab measurements were found and checks if new batches we created or updated
  private updateBatchState(product: Iproduct): void {
    this.projectService.updateBatchState({productId: String(product.id)}).subscribe({
      next: (response: UpdateBatchStateModel) => {
        if (response.createdBatches > 0 && response.reload) {
          // trigger project page batch reload when new batches were found
          this.batchStateUpdateEvent.emit(response);
          this.showMsg('#' + response.createdBatches + ' ' + this.translate.instant('dashboard.updatedForecastBatchesFound'));
        } else {
          this.showMsg(this.translate.instant('dashboard.noNewForecastBatchesFound'));
        }
      },
      error: () => {
        this.loadingLabMeasurements = false;
        this.showMsg(this.translate.instant('dashboard.errorUpdatingForecastMeasurements'), 5000, 'assertive');
      },
      complete: () => {
        this.loadingLabMeasurements = false;
      },
    })
  }

  private showMsg(msg: string, duration?: number, politeness?: AriaLivePoliteness): void {
    this.snackBar.open(msg, 'Close', {
      duration: duration ? duration : 5000,
      politeness: politeness ? politeness : 'off'
    });
  }
}
